<script setup>
import { computed, ref } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import FileIcon from 'next/icon/FileIcon.vue';
import Icon from 'next/icon/Icon.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import { MESSAGE_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Auth from 'dashboard/api/auth';

const { content, attachments, contentAttributes, messageType } =
  useMessageContext();

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const renderOriginal = ref(false);

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return content.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return content.value;
});

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});

// F1-T6: anexos winker como REFERENCIA (so metadados em content_attributes,
// nenhum blob no ActiveStorage). O store camelCasa chaves aninhadas
// (winker_attachments -> winkerAttachments); fallback p/ a forma snake por robustez.
const winkerAttachments = computed(() => {
  const ca = contentAttributes.value ?? {};
  const list = ca.winkerAttachments ?? ca.winker_attachments;
  return Array.isArray(list) ? list : [];
});

// FileIcon espera a extensao (pdf/doc/...); deriva do nome (tipo pode ser MIME).
const extensionOf = attachment => {
  const name = attachment?.nome ?? '';
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
};

// F2-T5 (esquema B, LAZY): nada é buscado no S3 até o agente CLICAR no placeholder —
// evita leitura/banda do S3/CDN de anexo que nunca é aberto (perf/custo). Ao clicar,
// pedimos (autenticado → carteira-check) uma URL de REDENÇÃO assinada; imagem/vídeo
// passam a usar `<img/video src>` INLINE no balão (o redeem faz 302 pro presigned S3,
// range nativo — nada trafega pelo Rails); outros tipos baixam (disposition=attachment).
// O token não vai em header (elemento de mídia não manda), por isso a URL assinada.
const route = useRoute();
const { t } = useI18n();
const signedUrls = ref({});
const downloadingId = ref(null);

const IMAGE_EXT = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'];
const VIDEO_EXT = ['mp4', 'webm', 'mov', 'ogg', 'm4v'];

const winkerAuthHeaders = () => {
  const data = Auth.getAuthData?.() ?? {};
  return {
    'access-token': data['access-token'],
    'token-type': data['token-type'],
    client: data.client,
    expiry: data.expiry,
    uid: data.uid,
  };
};

const mediaKind = attachment => {
  const tipo = (attachment?.tipo ?? '').toLowerCase();
  if (tipo.startsWith('image/')) return 'image';
  if (tipo.startsWith('video/')) return 'video';
  const ext = extensionOf(attachment);
  if (IMAGE_EXT.includes(ext)) return 'image';
  if (VIDEO_EXT.includes(ext)) return 'video';
  return 'other';
};

// O nome do param varia por rota: inbox/label/custom_view usam conversation_id (snake);
// team/mentions/unattended usam conversationId (camel). Ambos = display_id.
const conversationDisplayId = () =>
  route.params.conversation_id ?? route.params.conversationId;

const fetchSignedUrl = async (attachment, forDownload) => {
  const base = `/api/v1/accounts/${route.params.accountId}/winker_attachments/${conversationDisplayId()}/${attachment.id}`;
  const url = forDownload ? `${base}?disposition=attachment` : base;
  const response = await fetch(url, { headers: winkerAuthHeaders() });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return (await response.json()).url;
};

const triggerDownload = url => {
  const link = document.createElement('a');
  link.href = url;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
};

// Já-inline: clicar na imagem exibida amplia em nova aba.
const openImageFull = attachment => {
  const url = signedUrls.value[attachment.id];
  if (url) window.open(url, '_blank');
};

// Clique no PLACEHOLDER: mídia -> carrega e passa a exibir inline; outros -> baixa.
// É aqui (e só aqui) que se toca no S3.
const handleAttachmentClick = async attachment => {
  if (downloadingId.value != null) return;
  downloadingId.value = attachment.id;
  try {
    if (mediaKind(attachment) === 'other') {
      triggerDownload(await fetchSignedUrl(attachment, true));
    } else {
      signedUrls.value = {
        ...signedUrls.value,
        [attachment.id]: await fetchSignedUrl(attachment, false),
      };
    }
  } catch {
    useAlert(t('GALLERY_VIEW.ERROR_DOWNLOADING'));
  } finally {
    downloadingId.value = null;
  }
};

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <BaseBubble class="px-4 py-3" data-bubble-name="text">
    <div class="gap-3 flex flex-col">
      <span v-if="isEmpty" class="text-n-slate-11">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>
      <FormattedContent v-if="renderContent" :content="renderContent" />
      <TranslationToggle
        v-if="hasTranslations"
        class="-mt-3"
        :showing-original="renderOriginal"
        @toggle="handleSeeOriginal"
      />
      <AttachmentChips :attachments="attachments" class="gap-2" />
      <div v-if="winkerAttachments.length" class="flex flex-col gap-2">
        <template
          v-for="(attachment, index) in winkerAttachments"
          :key="attachment.id ?? index"
        >
          <!-- imagem inline (clique amplia em nova aba) -->
          <button
            v-if="
              mediaKind(attachment) === 'image' && signedUrls[attachment.id]
            "
            type="button"
            class="overflow-hidden rounded-lg cursor-pointer w-fit"
            :title="attachment.nome"
            @click="openImageFull(attachment)"
          >
            <img
              :src="signedUrls[attachment.id]"
              :alt="attachment.nome"
              class="object-cover max-w-xs max-h-64"
            />
          </button>
          <!-- vídeo inline (range nativo do S3) -->
          <video
            v-else-if="
              mediaKind(attachment) === 'video' && signedUrls[attachment.id]
            "
            :src="signedUrls[attachment.id]"
            controls
            class="rounded-lg max-w-xs max-h-64"
          />
          <!-- outros tipos (ou mídia que falhou o inline): chip de download -->
          <button
            v-else
            type="button"
            :disabled="downloadingId != null"
            class="flex items-center gap-2 h-9 px-2 overflow-hidden rounded-lg border border-n-container bg-n-alpha-white cursor-pointer hover:bg-n-alpha-2 disabled:opacity-60 disabled:cursor-wait w-fit"
            :title="attachment.nome"
            @click="handleAttachmentClick(attachment)"
          >
            <FileIcon
              class="flex-shrink-0"
              :file-type="extensionOf(attachment)"
            />
            <span
              class="flex-1 min-w-0 text-sm truncate max-w-36 text-n-slate-12"
            >
              {{ attachment.nome }}
            </span>
            <span
              class="flex-shrink-0 grid size-9 place-content-center text-n-slate-10"
            >
              <Icon
                :icon="
                  downloadingId === attachment.id
                    ? 'i-lucide-loader-circle'
                    : 'i-lucide-download'
                "
                :class="{ 'animate-spin': downloadingId === attachment.id }"
              />
            </span>
          </button>
        </template>
      </div>
      <template v-if="isTemplate">
        <div
          v-if="contentAttributes.submittedEmail"
          class="px-2 py-1 rounded-lg bg-n-alpha-3"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
